import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { removeFromCart, updateQty, clearCart } from '../../../shared/store/cartSlice';

const API_STATIC = (process.env.REACT_APP_API_URL ?? 'http://localhost:8000/api/v1').replace(/\/api\/v1.*$/, '');

const CartPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const items = useAppSelector(s => s.cart.items);
    const isAuthenticated = useAppSelector(s => s.auth.isAuthenticated);
    const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);
    const totalItems = items.reduce((sum, i) => sum + i.qty, 0);

    const handleProceedToCheckout = () => {
        if (!isAuthenticated) {
            navigate('/login?redirect=/cart');
            return;
        }
        navigate('/checkout');
    };

    return (
        <div style={{ background: '#f8fafc', minHeight: '60vh', padding: '48px 0 80px', fontFamily: "'Open Sans', sans-serif" }}>
            <div className="container">

                {/* Breadcrumb */}
                <nav style={{ marginBottom: 32, fontSize: '0.85rem', color: '#6b7280' }}>
                    <Link to="/" style={{ color: '#1565c0', textDecoration: 'none' }}>Home</Link>
                    <span style={{ margin: '0 8px' }}>›</span>
                    <span style={{ color: '#1a202c', fontWeight: 600 }}>Cart</span>
                </nav>

                <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1a202c', marginBottom: 32 }}>
                    My Cart {totalItems > 0 && <span style={{ fontSize: '1rem', fontWeight: 500, color: '#64748b' }}>({totalItems} item{totalItems !== 1 ? 's' : ''})</span>}
                </h1>

                {items.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '80px 20px', color: '#94a3b8' }}>
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 20 }}>
                            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                        </svg>
                        <h2 style={{ fontSize: '1.3rem', color: '#475569', marginBottom: 8 }}>Your cart is empty</h2>
                        <p style={{ marginBottom: 24 }}>Looks like you haven't added anything yet.</p>
                        <Link to="/products" style={{
                            display: 'inline-flex', alignItems: 'center', gap: 8,
                            padding: '11px 28px', background: '#1565c0', color: '#fff',
                            borderRadius: 50, fontWeight: 600, fontSize: '0.95rem', textDecoration: 'none',
                        }}>
                            Browse Products
                        </Link>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 28, alignItems: 'start' }}>

                        {/* Items */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {items.map(item => (
                                <div key={item.productId} style={{
                                    background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
                                    padding: '20px', display: 'grid',
                                    gridTemplateColumns: '80px 1fr auto',
                                    gap: 20, alignItems: 'center',
                                }}>
                                    {/* Thumbnail */}
                                    <div style={{ width: 80, height: 80, borderRadius: 8, overflow: 'hidden', background: '#f8fafc', flexShrink: 0 }}>
                                        <img
                                            src={`${API_STATIC}${item.image}`}
                                            alt={item.name}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            onError={e => {
                                                (e.target as HTMLImageElement).src =
                                                    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"%3E%3Crect width="80" height="80" fill="%23f0f4f8"/%3E%3C/svg%3E';
                                            }}
                                        />
                                    </div>

                                    {/* Info */}
                                    <div>
                                        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                            {item.category}
                                        </span>
                                        <h3 style={{ fontSize: '0.98rem', fontWeight: 600, color: '#1a202c', margin: '3px 0 2px' }}>
                                            {item.name}
                                        </h3>
                                        <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 12px' }}>{item.brand}</p>

                                        {/* Qty stepper */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                                            <button
                                                onClick={() => dispatch(updateQty({ productId: item.productId, qty: item.qty - 1 }))}
                                                style={stepBtn}
                                                aria-label="Decrease quantity"
                                            >−</button>
                                            <span style={{
                                                minWidth: 36, textAlign: 'center', fontSize: '0.9rem',
                                                fontWeight: 600, color: '#1a202c', padding: '6px 0',
                                                border: '1px solid #e2e8f0', borderLeft: 'none', borderRight: 'none',
                                            }}>
                                                {item.qty}
                                            </span>
                                            <button
                                                onClick={() => dispatch(updateQty({ productId: item.productId, qty: item.qty + 1 }))}
                                                style={stepBtn}
                                                aria-label="Increase quantity"
                                            >+</button>
                                        </div>
                                    </div>

                                    {/* Price + remove */}
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12 }}>
                                        <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a202c' }}>
                                            ₹{(item.price * item.qty).toLocaleString('en-IN')}
                                        </span>
                                        <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                                            ₹{item.price.toLocaleString('en-IN')} each
                                        </span>
                                        <button
                                            onClick={() => dispatch(removeFromCart(item.productId))}
                                            style={{
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                color: '#dc2626', fontSize: '0.82rem', fontWeight: 600,
                                                display: 'flex', alignItems: 'center', gap: 4, padding: 0,
                                            }}
                                            aria-label="Remove from cart"
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                            </svg>
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}

                            <button
                                onClick={() => dispatch(clearCart())}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: '#94a3b8', fontSize: '0.82rem', alignSelf: 'flex-start',
                                    padding: 0, textDecoration: 'underline',
                                }}
                            >
                                Clear cart
                            </button>
                        </div>

                        {/* Summary */}
                        <div style={{
                            background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
                            padding: 28, position: 'sticky', top: 100,
                        }}>
                            <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a202c', margin: '0 0 20px' }}>
                                Order Summary
                            </h2>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: '0.9rem', color: '#4b5563' }}>
                                <span>Items ({totalItems})</span>
                                <span>₹{subtotal.toLocaleString('en-IN')}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: '0.9rem', color: '#4b5563' }}>
                                <span>Shipping</span>
                                <span style={{ color: '#16a34a', fontWeight: 600 }}>To be calculated</span>
                            </div>

                            <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '16px 0' }} />

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24, fontSize: '1.05rem', fontWeight: 700, color: '#1a202c' }}>
                                <span>Subtotal</span>
                                <span>₹{subtotal.toLocaleString('en-IN')}</span>
                            </div>

                            <button
                                onClick={handleProceedToCheckout}
                                style={{
                                    width: '100%', padding: '13px 0', background: '#1565c0', color: '#fff',
                                    border: 'none', borderRadius: 50, fontWeight: 700, fontSize: '1rem',
                                    cursor: 'pointer', marginBottom: 12,
                                    transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#1251a3'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#1565c0'; }}
                            >
                                Proceed to Checkout →
                            </button>

                            <Link to="/products" style={{
                                display: 'block', textAlign: 'center', color: '#6b7280',
                                fontSize: '0.88rem', textDecoration: 'none', fontWeight: 500,
                            }}>
                                ← Continue Shopping
                            </Link>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @media (max-width: 768px) {
                    .container > div[style*="grid-template-columns: 1fr 340px"] {
                        grid-template-columns: 1fr !important;
                    }
                    .container > div[style*="grid-template-columns: 80px"] {
                        grid-template-columns: 64px 1fr !important;
                        gap: 12px !important;
                    }
                }
            `}</style>
        </div>
    );
};

const stepBtn: React.CSSProperties = {
    width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer',
    fontSize: '1rem', fontWeight: 700, color: '#374151', borderRadius: 0,
    transition: 'background 0.1s',
};

export default CartPage;
